import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { FormikProvider, useFormikContext } from 'formik';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Checkbox from '@components/Checkbox/Checkbox';
import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import Icon from '@components/Icon/Icon';
import RoundedButton from '@components/RoundedButton';

import {
  useAssociateRelatedRecord,
  useDisassociateRelatedRecord,
  useGetPatientHealthSummaryRecords,
} from '@hooks/api/patient-record';
import { useCuration } from '@hooks/useCuration';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import type { GenericRecord } from '@models/generic-record';

import { formatName } from '@utils/format';

import type { GetPatientHealthSummaryRecordsQueryVariables } from 'generated/patient-record.generated';
import type { ResourceAssociationUnion } from 'generated/types';

import { CheckboxContainer } from './RelatedRecordsContent.style';

type RelatedRecordType = keyof Omit<
  GetPatientHealthSummaryRecordsQueryVariables,
  'patient_id'
>;

const messages = defineMessages({
  records: {
    id: 'relatedRecordsContent.records',
    defaultMessage: 'Search for {name} Records',
  },
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
  noRecords: {
    id: 'relatedRecordsContent.noRecords',
    defaultMessage: 'No Records',
  },
  errorMessage: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  // Friendly record names
  allergy: {
    id: 'relatedRecordsContent.allergy',
    defaultMessage: 'Allergy',
  },
  appointment: {
    id: 'relatedRecordsContent.appointment',
    defaultMessage: 'Appointment',
  },
  emergency: {
    id: 'relatedRecordsContent.emergency',
    defaultMessage: 'Emergency Visit',
  },
  familyHistory: {
    id: 'relatedRecordsContent.familyHistory',
    defaultMessage: 'Family History',
  },
  geneticInfo: {
    id: 'relatedRecordsContent.geneticInfo',
    defaultMessage: 'Genetic Info',
  },
  hospitalization: {
    id: 'relatedRecordsContent.hospitalization',
    defaultMessage: 'Hospitalization',
  },
  imaging: {
    id: 'relatedRecordsContent.imaging',
    defaultMessage: 'Imaging and Test',
  },
  immunization: {
    id: 'relatedRecordsContent.immunization',
    defaultMessage: 'Immunization',
  },
  labObservation: {
    id: 'relatedRecordsContent.labObservation',
    defaultMessage: 'Lab Observation',
  },
  labResult: {
    id: 'relatedRecordsContent.labResult',
    defaultMessage: 'Lab Result',
  },
  condition: {
    id: 'relatedRecordsContent.condition',
    defaultMessage: 'Medical Condition',
  },
  medication: {
    id: 'relatedRecordsContent.medication',
    defaultMessage: 'Medication',
  },
  surgery: {
    id: 'relatedRecordsContent.surgery',
    defaultMessage: 'Surgery',
  },
  vitalSign: {
    id: 'relatedRecordsContent.vitalSign',
    defaultMessage: 'Vital Sign',
  },
  body: {
    id: 'relatedRecordsContent.body',
    defaultMessage: 'Body Observation',
  },
  selectRecordType: {
    id: 'relatedRecordsContent.selectRecordType',
    defaultMessage: 'Select a record type',
  },
});

const RelatedRecordsContent: React.FC<{
  /**
   * Callback to close the modal
   */
  onClose?: () => void;
  /**
   * Resource's related records (only when creating)
   */
  associations?: string[];
}> = ({ associations: associatedResources, onClose }) => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const formik = useFormikContext();
  const { setFieldValue } = formik;
  const [relatedRecordType, setRelatedRecordType] =
    useState<RelatedRecordType>();

  const {
    state: { activeProfile },
  } = useSession();
  const patientName = formatName(
    activeProfile?.first_name,
    activeProfile?.last_name
  );
  const {
    state: { isCreating, record },
    setRecord,
  } = useCuration();

  const associatedRecords = React.useMemo(
    () => record?.associatedRecords ?? [],
    [record?.associatedRecords]
  );

  const [selectedRecords, setSelectedRecords] = useState<string[]>(
    isCreating
      ? (associatedResources ?? [])
      : associatedRecords.map(({ id, resourceType }) => `${resourceType}/${id}`)
  );
  const [associations, setAssociations] = useState<string[]>([]);
  const [disassociations, setDisassociations] = useState<string[]>([]);

  const [createAssociations, createAssociationsLoading] =
    useAssociateRelatedRecord();
  const [deleteAssociations, deleteAssociationsLoading] =
    useDisassociateRelatedRecord();

  const {
    data: {
      allergy,
      appointment,
      body_observation,
      condition,
      emergency_visits,
      family_member_history,
      genetic_information_observation,
      hospitalizations: hospitalizationsData,
      immunization,
      medication_statement,
      procedure,
      vital_signs_observation,
      imaging_report,
      lab_report,
    },
    loading: recordsLoading,
  } = useGetPatientHealthSummaryRecords(
    relatedRecordType
      ? {
          [relatedRecordType]: true,
        }
      : undefined
  );

  const options: { [key in RelatedRecordType]?: string } = {
    allergy: formatMessage(messages.allergy),
    appointment: formatMessage(messages.appointment),
    body_observation: formatMessage(messages.body),
    condition: formatMessage(messages.condition),
    imaging_report: formatMessage(messages.imaging),
    lab_report: formatMessage(messages.labResult),
    emergency_visits_encounter: formatMessage(messages.emergency),
    family_member_history: formatMessage(messages.familyHistory),
    genetic_information_observation: formatMessage(messages.geneticInfo),
    hospitalizations_encounter: formatMessage(messages.hospitalization),
    immunization: formatMessage(messages.immunization),
    medication_statement: formatMessage(messages.medication),
    procedure: formatMessage(messages.surgery),
    vital_signs_observation: formatMessage(messages.vitalSign),
  };

  const loading =
    recordsLoading || createAssociationsLoading || deleteAssociationsLoading;

  const allergies = allergy.map<GenericRecord>((a) => ({
    id: `${a.resourceType}/${a.id}`,
    title: a.substance,
    type: a.resourceType,
    friendlyType: formatMessage(messages.allergy),
    date: a.getLastKnownReactionDate(),
    hcpName: null,
    surveyId: 'allergies',
  }));

  const appointments = appointment.map<GenericRecord>((a) => ({
    id: `${a.resourceType}/${a.id}`,
    title: a.visitReason,
    type: a.resourceType,
    friendlyType: formatMessage(messages.appointment),
    date: a.getVisitDate(),
    hcpName: a.hcpName,
    surveyId: 'appointments',
  }));

  const bodyObservations = body_observation.map<GenericRecord>((b) => ({
    id: `${b.resourceType}/${b.id}`,
    title: b.displayName ?? '-',
    type: b.resourceType,
    friendlyType: formatMessage(messages.body),
    date: b.getObservationDate(),
    hcpName: null,
    surveyId: 'body',
  }));

  const emergencyVisits = emergency_visits.map<GenericRecord>((e) => ({
    id: `${e.resourceType}/${e.id}`,
    title: e.visitReason,
    type: e.resourceType,
    friendlyType: formatMessage(messages.emergency),
    date: e.getVisitDate(),
    hcpName: e.practitioner,
    surveyId: 'emergency-visits',
  }));

  const familyHistory = family_member_history.map<GenericRecord>((f) => ({
    id: `${f.resourceType}/${f.id}`,
    title: f.conditions.map(({ name }) => name).join(', '),
    type: f.resourceType,
    friendlyType: formatMessage(messages.familyHistory),
    date: f.getOnsetDate(),
    hcpName: null,
    surveyId: 'family-history',
  }));

  const geneticInfo = genetic_information_observation.map<GenericRecord>(
    (g) => ({
      id: `${g.resourceType}/${g.id}`,
      title: g.displayName ?? '-',
      type: g.resourceType,
      friendlyType: formatMessage(messages.geneticInfo),
      date: g.getObservationDate(),
      hcpName: null,
      surveyId: 'genetic-information-carrier-status',
    })
  );

  const hospitalizations = hospitalizationsData.map<GenericRecord>((e) => ({
    id: `${e.resourceType}/${e.id}`,
    title: e.visitReason,
    type: e.resourceType,
    friendlyType: formatMessage(messages.hospitalization),
    date: e.getVisitDate(),
    hcpName: e.practitioner,
    surveyId: 'hospitalizations',
  }));

  const imagingTests = imaging_report.map<GenericRecord>((d) => ({
    id: `${d.resourceType}/${d.id}`,
    title: d.testName ?? d.typeName ?? '',
    type: d.resourceType,
    friendlyType: formatMessage(messages.imaging),
    date: d.getTestDate(),
    hcpName: d.practitioner,
    surveyId: 'imaging-and-tests',
  }));

  const immunizations = immunization.map<GenericRecord>((i) => ({
    id: `${i.resourceType}/${i.id}`,
    title: i.vaccineName ?? '',
    type: i.resourceType,
    friendlyType: formatMessage(messages.immunization),
    date: i.getImmunizationDate(),
    hcpName: i.practitioner,
    surveyId: 'immunizations',
  }));

  const labResults = lab_report.map<GenericRecord>((d) => ({
    id: `${d.resourceType}/${d.id}`,
    title: d.typeName ?? '',
    type: d.resourceType,
    friendlyType: formatMessage(messages.labResult),
    date: d.getTestDate(),
    hcpName: d.practitioner,
    surveyId: 'labs',
  }));

  const medicalConditions = condition.map<GenericRecord>((c) => ({
    id: `${c.resourceType}/${c.id}`,
    title: c.condition ?? '',
    type: c.resourceType,
    friendlyType: formatMessage(messages.condition),
    date: c.getOnsetDate(),
    hcpName: null,
    surveyId: 'medical-conditions',
  }));

  const medications = medication_statement.map<GenericRecord>((m) => ({
    id: `${m.resourceType}/${m.id}`,
    title: m.medication ?? '',
    type: m.resourceType,
    friendlyType: formatMessage(messages.medication),
    date: m.getDateStart(),
    hcpName: null,
    surveyId: 'medications',
  }));

  const surgeries = procedure.map<GenericRecord>((p) => ({
    id: `${p.resourceType}/${p.id}`,
    title: p.procedureName,
    type: p.resourceType,
    friendlyType: formatMessage(messages.surgery),
    date: p.getProcedureDate(),
    hcpName: p.practitioner,
    surveyId: 'surgeries',
  }));

  const vitalSigns = vital_signs_observation.map<GenericRecord>((v) => ({
    id: `${v.resourceType}/${v.id}`,
    title: v.displayName ?? '-',
    type: v.resourceType,
    friendlyType: formatMessage(messages.vitalSign),
    date: v.getObservationDate(),
    hcpName: null,
    surveyId: 'vital-signs',
  }));

  const data = allergies
    .concat(appointments)
    .concat(bodyObservations)
    .concat(emergencyVisits)
    .concat(familyHistory)
    .concat(geneticInfo)
    .concat(hospitalizations)
    .concat(imagingTests)
    .concat(immunizations)
    .concat(labResults)
    .concat(medicalConditions)
    .concat(medications)
    .concat(surgeries)
    .concat(vitalSigns);

  const filteredData = data.filter(
    (resource) => resource.id !== record?.getResourceId()
  );

  const handleSelection = (resourceId: string) => {
    const recordsCopy = [...selectedRecords];

    const existingRecord = recordsCopy.find((record) => record === resourceId);
    if (existingRecord) {
      setSelectedRecords(recordsCopy.filter((record) => record !== resourceId));
      setAssociations((prev) => prev.filter((id) => id !== resourceId));
      setDisassociations((prev) => [...prev, resourceId]);
    } else {
      setSelectedRecords([...recordsCopy, resourceId]);
      setAssociations((prev) => [...prev, resourceId]);
      setDisassociations((prev) => prev.filter((id) => id !== resourceId));
    }
  };

  const handleChange = () => {
    setFieldValue('associatedResources', associations);
    onClose?.();
  };

  const handleRecordTypeChange = (e: SelectChangeEvent<RelatedRecordType>) => {
    if (!e.target.value) {
      setRelatedRecordType(undefined);
    }
    setRelatedRecordType(e.target.value as RelatedRecordType);
  };

  const handleSubmit = async () => {
    const formattedResource = `${record?.resourceType}/${record?.id}`;
    if (!formattedResource) return;

    try {
      let associatedRecords: ResourceAssociationUnion[] = [];
      if (associations.length) {
        const { data } = await createAssociations({
          input: {
            resource: formattedResource,
            associations,
          },
        });

        associatedRecords =
          data?.postResourceAssociation.associatedResources ?? [];
      }
      if (disassociations.length) {
        const { data } = await deleteAssociations({
          input: {
            resource: formattedResource,
            associations: disassociations,
          },
        });
        associatedRecords =
          data?.removeResourceAssociation.associatedResources ?? [];
      }
      if (record) {
        record.associatedRecords = associatedRecords;
        setRecord(record);
      }
    } catch {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.errorMessage),
      });
    } finally {
      onClose?.();
    }
  };

  return (
    <FormikProvider value={formik}>
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 11.5 }}>
          <Typography color="primary.main" variant="h5">
            {formatMessage(messages.records, {
              name: `${patientName ? `${patientName}'s` : ''}`,
            })}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 0.5 }}>
          <Icon clickable name="xmark" onClick={onClose} size="lg" type="fas" />
        </Grid2>
        <Grid2>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <InputLabel
              id={'record-type-select-label'}
              sx={{ pr: 2, whiteSpace: 'wrap' }}
            >
              {formatMessage(messages.selectRecordType) + ':'}
            </InputLabel>
            <Select
              displayEmpty
              labelId={'record-type-select-label'}
              onChange={handleRecordTypeChange}
              value={relatedRecordType ?? ''}
            >
              <MenuItem key={''} value={''}>
                {'Select...'}
              </MenuItem>
              {Object.keys(options).map((key) => (
                <MenuItem key={key} value={key}>
                  {options[key as RelatedRecordType]}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Grid2>
      </Grid2>

      <ContentLoader loading={loading}>
        <MuiDataTable
          columns={[
            {
              name: 'id',
              options: {
                customHeadLabelRender: () => null,
                customBodyRender: (id: string) => (
                  <CheckboxContainer>
                    <Checkbox
                      checked={selectedRecords.includes(id)}
                      onChange={() => handleSelection(id)}
                    />
                  </CheckboxContainer>
                ),
              },
            },
            {
              name: 'title',
              label: 'Record Name',
              className: 'blue-hover',
              options: {
                customBodyRender: (title: string) => (
                  <Typography>{title}</Typography>
                ),
                searchable: true,
                sort: true,
              },
            },
            {
              name: 'date',
              label: 'Date',
              className: 'date',
              options: {
                filter: true,
                sort: true,
              },
            },
            {
              name: 'friendlyType',
              label: 'Record Type',
              className: 'small',
            },
            {
              name: 'hcpName',
              label: 'Practitioner',
              className: 'small',
              options: {
                filter: true,
                searchable: true,
                sort: true,
              },
            },
          ]}
          data={filteredData}
          options={{
            search: true,
            sort: true,
            filter: true,
            sortOrder: {
              direction: 'desc',
              name: 'date',
            },
            textLabels: {
              body: {
                noMatch: formatMessage(messages.noRecords),
              },
            },
          }}
        />
      </ContentLoader>

      <RoundedButton
        disabled={loading}
        onClick={isCreating ? handleChange : handleSubmit}
      >
        {formatMessage(messages.submit)}
      </RoundedButton>
    </FormikProvider>
  );
};

export default RelatedRecordsContent;
